import { ActionTree } from 'vuex'

import api from '@/apiClient'

import { Portion, PortionContext, PortionOuterContext, PortionResourcePayload } from '@/models'

import { assertToken, mergeById } from '@/utils'

import { RootState } from '../../state'
import { MenuState } from '../state'

import {
  ADD_PORTION,
  INCREMENT_ITEM_PORTIONS_QUERY_INDEX,
  LOADED_ALL_ITEM_PORTIONS,
  RESET_ITEM_PORTIONS_QUERY_INDEX,
  UPDATE_ITEM_PORTIONS,
  UPDATE_ITEM_PORTIONS_TOTAL,
  UPDATE_PORTIONS
} from './mutations'

const actions: ActionTree<MenuState, RootState> = {
  /**
   * Adds a new Portion and updates an Item relation.
   *
   * @param portion Portion to be added.
   * @param itemId Item id in which the Portion will be added.
   */
  addPortion (
    { commit, state },
    { portion, itemId }: { portion: Portion; itemId: number }
  ) {
    const itemPortions = state.items.portions[itemId]

    if (itemPortions === undefined) {
      throw new Error(`Portions relation for Item "${itemId}" was not found`)
    }

    commit(ADD_PORTION, portion)

    commit(UPDATE_ITEM_PORTIONS_TOTAL, {
      itemId,
      total: itemPortions.total + 1
    })

    commit(UPDATE_ITEM_PORTIONS, {
      itemId,
      portions: [...itemPortions.ids, portion.id]
    })
  },

  /**
   * Creates a Portion.
   *
   * It rejects if the user is not logged in.
   *
   * @param payload Payload to create the Portion.
   */
  async createPortion ({ dispatch, rootState, rootGetters }, payload: PortionResourcePayload) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const portion = await api.menu.portions.createPortion(tokenV2, providerId, businessUnit.id, payload.itemId, payload)

    await dispatch('addPortion', { portion, itemId: payload.itemId })

    return portion
  },

  /**
   * Loads one Portion.
   *
   * It rejects if the user is not logged in.
   *
   * It loads the Item if it is not loaded.
   *
   * @param context PortionContext to be loaded.
   */
  async loadPortion (
    { commit, dispatch, rootState, rootGetters, state },
    { id, itemId, itemGroupId }: PortionContext
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    let itemPortions = state.items.portions[itemId]

    if (itemPortions === undefined) {
      await dispatch('loadItem', { id: itemId, itemGroupId })
      itemPortions = state.items.portions[itemId]
    }

    const portion = await api.menu.portions.fetchPortion(tokenV2, providerId, businessUnit.id, itemId, id)

    if (!itemPortions.ids.has(portion.id)) {
      await dispatch('addPortion', { portion, itemId })
    } else {
      const portions = mergeById(state.portions.elements, [portion])
      commit(UPDATE_PORTIONS, portions)
    }
  },

  /**
   * Loads a page of Portions.
   *
   * It rejects if the user is not logged in.
   *
   * It loads the Item if it is not loaded.
   *
   * When a previously loaded page changes, it desynchronizes and will load
   * an empty page after the last page, which will reset the query index.
   *
   * @param itemId Item id whose Portions will be loaded.
   */
  async loadPortionPage (
    { commit, dispatch, rootState, rootGetters, state },
    { itemId, itemGroupId }: PortionOuterContext
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    let itemPortions = state.items.portions[itemId]

    if (itemPortions === undefined) {
      await dispatch('loadItem', { id: itemId, itemGroupId })
      itemPortions = state.items.portions[itemId]
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const page = await api.menu.portions.fetchPortionPage(
      tokenV2,
      providerId,
      businessUnit.id,
      itemId,
      {
        ...itemPortions.query
      }
    )

    if (itemPortions.total !== page.total) {
      commit(UPDATE_ITEM_PORTIONS_TOTAL, { itemId, total: page.total })
    }

    if (page.elements.length > 0) {
      const portions = mergeById(state.portions.elements, page.elements)

      commit(UPDATE_PORTIONS, portions)

      const ids = [
        ...itemPortions.ids,
        ...page.elements.map((i) => i.id)
      ]

      commit(UPDATE_ITEM_PORTIONS, { itemId, portions: ids })

      if ([...new Set(ids)].length === page.total) {
        commit(LOADED_ALL_ITEM_PORTIONS, itemId)
      }

      commit(INCREMENT_ITEM_PORTIONS_QUERY_INDEX, {
        itemId,
        offset: page.elements.length
      })
    } else {
      commit(RESET_ITEM_PORTIONS_QUERY_INDEX, itemId)

      if (page.total === 0 && itemPortions.ids.size > 0) {
        commit(UPDATE_ITEM_PORTIONS, { itemId, portions: [] })
      }
    }
  },

  /**
   * Updates an Portion.
   *
   * It rejects if the user is not logged in.
   *
   * @param id Portion id to be updated.
   * @param payload Payload to update it.
   */
  async updatePortion (
    { commit, rootState, rootGetters, state },
    { id, payload }: { id: number; payload: PortionResourcePayload }
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const portion = await api.menu.portions.updatePortion(tokenV2, providerId, businessUnit.id, payload.itemId, id, payload)

    const portions = mergeById(state.portions.elements, [portion])

    commit(UPDATE_PORTIONS, portions)
  }
}

export default actions
